import { Component } from 'react';
import { GeneratorLayout, genStyles } from '../converter-layout/generator-layout';
import { CodeBlock } from '../converter-layout/code-block';

type OutputFormat = 'dataUri' | 'imgTag' | 'cssBackground' | 'markdown' | 'rawBase64';

interface State {
    dataUri: string;
    fileName: string;
    fileSize: number;
    mimeType: string;
    outputFormat: OutputFormat;
    error: string | null;
    copied: boolean;
}

export default class ImageToBase64Component extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        dataUri: '',
        fileName: '',
        fileSize: 0,
        mimeType: '',
        outputFormat: 'dataUri',
        error: null,
        copied: false,
    };

    handleFile = (file: File) => {
        if (!file.type.startsWith('image/')) {
            this.setState({ error: `Unsupported file type: ${file.type || 'unknown'}. Please upload an image.` });
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const dataUri = String(reader.result);
            this.setState({
                dataUri,
                fileName: file.name,
                fileSize: file.size,
                mimeType: file.type,
                error: null,
                copied: false,
            });
        };
        reader.onerror = () => this.setState({ error: 'Failed to read file. Please try again.' });
        reader.readAsDataURL(file);
    };

    onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (f) this.handleFile(f);
    };

    onDrop = (e: React.DragEvent) => {
        e.preventDefault();
        const f = e.dataTransfer.files?.[0];
        if (f) this.handleFile(f);
    };

    private formatOutput(): string {
        const { dataUri, mimeType, fileName, outputFormat } = this.state;
        if (!dataUri) return '';
        const base64 = dataUri.split(',')[1] || '';
        const altText = (fileName || 'image').replace(/\.[^.]+$/, '');
        switch (outputFormat) {
            case 'dataUri': return dataUri;
            case 'rawBase64': return base64;
            case 'imgTag': return `<img src="${dataUri}" alt="${altText}">`;
            case 'cssBackground': return `background-image: url("${dataUri}");`;
            case 'markdown': return `![${altText}](${dataUri})`;
        }
    }

    copyOutput = () => {
        const out = this.formatOutput();
        if (!out) return;
        navigator.clipboard.writeText(out).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    private fmtBytes(n: number): string {
        if (n < 1024) return `${n} B`;
        if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
        return `${(n / (1024 * 1024)).toFixed(2)} MB`;
    }

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    render() {
        const { dataUri, fileName, fileSize, mimeType, outputFormat, error, copied } = this.state;
        const output = this.formatOutput();
        const expandedSize = output.length;

        const form = (
            <>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Upload image</span></div>
                    <div
                        onDragOver={e => e.preventDefault()}
                        onDrop={this.onDrop}
                        style={{ border: '2px dashed #cbd5e1', borderRadius: 8, padding: '1.5rem', textAlign: 'center', cursor: 'pointer', background: '#f8fafc' }}
                    >
                        <input type="file" id="i2b-input" accept="image/*"
                            onChange={this.onInputChange} style={{ display: 'none' }} />
                        <label htmlFor="i2b-input" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                            {dataUri ? <>
                                <img src={dataUri} alt="preview" style={{ maxWidth: '100%', maxHeight: 160, borderRadius: 6 }} />
                                <span style={{ fontSize: '0.85rem', color: '#1e293b', fontWeight: 600 }}>{fileName}</span>
                            </> : <>
                                <span style={{ fontSize: '2.5rem', opacity: 0.4 }}>⇪</span>
                                <span style={{ color: '#1e293b' }}>Drop image here, or click to choose</span>
                                <span style={{ fontSize: '0.78rem', color: '#64748b' }}>PNG, JPG, GIF, WebP, SVG, ICO</span>
                            </>}
                        </label>
                    </div>
                    {dataUri && (
                        <div style={{ fontSize: '0.85rem', color: '#475569' }}>
                            <strong>Type:</strong> {mimeType}<br/>
                            <strong>Source size:</strong> {this.fmtBytes(fileSize)}<br/>
                            <strong>Base64 size:</strong> {this.fmtBytes(expandedSize)} <span style={{ color: '#94a3b8' }}>(+{Math.round(((expandedSize - fileSize) / fileSize) * 100)}%)</span>
                        </div>
                    )}
                </div>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Output format</span></div>
                    <div className={genStyles.field}>
                        <label htmlFor="i2b-fmt">Format</label>
                        <select id="i2b-fmt" value={outputFormat}
                            onChange={e => this.setState({ outputFormat: e.target.value as OutputFormat, copied: false })}>
                            <option value="dataUri">Data URI (data:image/png;base64,…)</option>
                            <option value="rawBase64">Raw base64 string</option>
                            <option value="imgTag">{'<img src="…" alt="…">'}</option>
                            <option value="cssBackground">{'CSS background-image: url(…)'}</option>
                            <option value="markdown">Markdown image link</option>
                        </select>
                    </div>
                </div>
            </>
        );

        return (
            <GeneratorLayout
                form={form}
                previewLabel="Encoded output"
                previewMeta={output ? `${output.length} chars` : undefined}
                preview={output ? (
                    <CodeBlock code={output}
                        language={outputFormat === 'imgTag' || outputFormat === 'markdown' ? 'html' : outputFormat === 'cssBackground' ? 'css' : null}
                        wrapLines maxHeight={500} minHeight={320} ariaLabel="Base64 output" />
                ) : (
                    <pre className={genStyles.previewArea}>Upload an image to see the encoded output here.</pre>
                )}
                previewActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyOutput} disabled={!output}>
                        {copied ? '✓ Copied' : '📋 Copy'}
                    </button>
                }
                error={error}
            />
        );
    }
}
